import { z } from 'zod';
import { isoTimestampSchema, uuidSchema } from './common';

/* Supplier intelligence (P1 §5.7, §10, ADR-0027). Ratios in basis points (10 000 = 100 %). */

const dateSchema = z.iso.date();

export const analyticsWindowQuerySchema = z.object({
  from: dateSchema,
  to: dateSchema,
  /** Minimum observations per metric before it counts (default 3). */
  min_samples: z.coerce.number().int().min(1).max(100).default(3),
});

export const metricSchema = z.object({
  value_bps: z.number().int().nullable(),
  samples: z.number().int(),
  sufficient: z.boolean(),
  formula: z.string(),
});

export const scorecardSchema = z.object({
  supplier_id: uuidSchema,
  supplier_name: z.string(),
  currency: z.string(),
  from: dateSchema,
  to: dateSchema,
  min_samples: z.number().int(),
  metrics: z.object({
    fill_rate: metricSchema,
    on_time: metricSchema,
    rejection_rate: metricSchema,
    price_variance: metricSchema,
    invoice_discrepancy: metricSchema,
  }),
  weights: z.record(z.string(), z.number().int()),
  /** 0–100 with one decimal; null when any weighted metric lacks data. */
  score: z.number().nullable(),
  insufficient: z.array(z.string()),
  volumes: z.object({
    purchase_orders: z.number().int(),
    receipts: z.number().int(),
    invoices: z.number().int(),
    received_value_minor: z.number().int(),
  }),
  ratings: z.array(
    z.object({
      id: uuidSchema,
      period: z.string(),
      score: z.number().int(),
      comment: z.string(),
      evidence: z.string().nullable(),
      rated_by_name: z.string().nullable(),
      created_at: isoTimestampSchema,
    }),
  ),
});
export type SupplierScorecard = z.infer<typeof scorecardSchema>;

export const supplierCompareQuerySchema = analyticsWindowQuerySchema.extend({
  item_id: uuidSchema,
});
export const supplierCompareResponseSchema = z.object({
  item_id: uuidSchema,
  item_name: z.string(),
  base_unit: z.enum(['g', 'ml', 'pcs']),
  /** Prices are normalised per display unit (kg / l / piece) of the item. */
  display_unit: z.string(),
  currency: z.string(),
  from: dateSchema,
  to: dateSchema,
  suppliers: z.array(
    z.object({
      supplier_id: uuidSchema,
      supplier_name: z.string(),
      receipts: z.number().int(),
      received_qty_scaled: z.number().int(),
      avg_price_minor: z.number().int().nullable(),
      last_price_minor: z.number().int().nullable(),
      last_received_at: isoTimestampSchema.nullable(),
      rejection_bps: z.number().int().nullable(),
      sufficient: z.boolean(),
    }),
  ),
});

export const priceHistoryQuerySchema = analyticsWindowQuerySchema.extend({
  item_id: uuidSchema.optional(),
});
export const priceHistoryResponseSchema = z.object({
  supplier_id: uuidSchema,
  currency: z.string(),
  points: z.array(
    z.object({
      item_id: uuidSchema,
      item_name: z.string(),
      display_unit: z.string(),
      business_date: dateSchema,
      receipt_number: z.string(),
      /** Net price per display unit, minor units. */
      price_minor: z.number().int(),
      qty_scaled: z.number().int(),
    }),
  ),
});

export const createSupplierRatingRequestSchema = z.object({
  period: z.string().regex(/^\d{4}-(0[1-9]|1[0-2])$/),
  score: z.number().int().min(1).max(5),
  comment: z.string().trim().min(3).max(500),
  evidence: z.string().trim().min(1).max(300).nullable().default(null),
});
